import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { StoreApi, UseBoundStore } from 'zustand';

import { StateWithExpanded } from 'stores/types';
import { colors } from 'styles/theme/colors';
import DropdownCaretIcon from 'svg/DropdownCaretIcon';

interface ChipBagProps<T extends StateWithExpanded> {
  children: React.ReactNode;
  useStore: UseBoundStore<StoreApi<T>>;
}

const ChipBag = <T extends StateWithExpanded>({ children, useStore }: ChipBagProps<T>) => {
  const expanded = useStore((state) => state.expanded);
  const { setExpanded } = useStore((state) => state.actions);

  const [chipsHidden, setChipsHidden] = useState(false);
  const [isWrapped, setIsWrapped] = useState(false);
  const [showMorePosition, setShowMorePosition] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const showMoreRef = useRef<HTMLDivElement>(null);
  const chips = useMemo(() => React.Children.toArray(children), [children]);

  const calculateFirstRowWidth = () => {
    const container = containerRef.current;
    if (!container) return 0;

    const chipElements = container.querySelector('[data-chip-container]')?.children;
    if (!chipElements || chipElements.length === 0) return 0;

    const firstRowY = (chipElements[0] as HTMLElement).offsetTop;

    const firstRowChips = Array.from(chipElements)
      .map((el) => el as HTMLElement)
      .filter((chip) => chip.offsetTop === firstRowY);

    return firstRowChips.reduce((total, chip, index) => {
      const gap = index > 0 ? 4 : 0;
      return total + chip.offsetWidth + gap;
    }, 0);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const checkWrap = () => {
      setChipsHidden(container.scrollHeight >= 48 && !expanded); // 48px is the height of 2 rows of chips
      setIsWrapped(container.scrollHeight >= 48);

      const firstRowWidth = calculateFirstRowWidth();
      setShowMorePosition(firstRowWidth + 4); // Add gap after last chip
    };

    checkWrap();

    const resizeObserver = new ResizeObserver(() => {
      checkWrap();
    });

    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [expanded, chips]);

  return (
    <AllContentContainer>
      <ChipContainer $expanded={expanded} ref={containerRef}>
        <Container data-chip-container>
          {chips}
          {expanded && isWrapped && (
            <ShowLink onClick={() => setExpanded(false)}>
              Show Less <DropdownCaretIcon style={{ transform: 'rotate(180deg)' }} />
            </ShowLink>
          )}
        </Container>
      </ChipContainer>

      {chipsHidden && !expanded && (
        <ShowMoreContainer ref={showMoreRef} $position={showMorePosition}>
          <ShowLink onClick={() => setExpanded(true)}>
            Show More <DropdownCaretIcon />
          </ShowLink>
        </ShowMoreContainer>
      )}
    </AllContentContainer>
  );
};

export default ChipBag;

const AllContentContainer = styled.div`
  width: 100%;
  position: relative;
`;

const ChipContainer = styled.div<{ $expanded: boolean }>`
  height: ${({ $expanded }) => ($expanded ? 'unset' : '24px')};
  min-height: ${({ $expanded }) => ($expanded ? 'auto' : '24px')};
  width: 85%;
  overflow: ${({ $expanded }) => ($expanded ? 'visible' : 'hidden')};
`;

const ShowMoreContainer = styled.div<{ $position: number }>`
  position: absolute;
  left: ${({ $position }) => $position}px;
  top: 5px;
  display: flex;
  align-items: center;
`;

const Container = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`;

const ShowLink = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 84px;

  padding: 0 4px;

  color: ${colors.primaryBlue};
  cursor: pointer;
  font-size: 12px;
  font-weight: 400;
  line-height: 12px;

  align-self: center;
`;
